import { useState, useEffect, useRef } from "react";
import {
  useActiveAccount,
  useActiveWallet,
  useConnectModal,
  useDisconnect,
  darkTheme,
} from "thirdweb/react";
import { createWallet } from "thirdweb/wallets";
import { chain, client } from "../../../utils/constants";

/**
 * Wallet connect button — hook-driven (not thirdweb's <ConnectButton>).
 *
 * Why: <ConnectButton> renders a PERSISTENT thirdweb component in the navbar
 * whose connected-wallet UI lazy-loads the wallet icon and re-renders on every
 * transaction. In dev those lazy chunks can 404 → the dynamic import resolves
 * to `undefined` → React throws "Element type is invalid" on each stake/list,
 * even when the on-chain action succeeds.
 *
 * This version renders ONLY our own markup and drives connect/disconnect via
 * thirdweb hooks. thirdweb's modal is opened transiently by useConnectModal()
 * (only while connecting), so no thirdweb component lives in the navbar tree
 * and the crash cannot occur — in dev or production.
 */
const wallets = [
  createWallet("io.metamask"),
  createWallet("com.trustwallet.app"),
  createWallet("org.uniswap"),
  createWallet("com.coinbase.wallet"),
  createWallet("com.ledger"),
  createWallet("app.phantom"),
  createWallet("com.enkrypt"),
];

const THEME = darkTheme({
  colors: {
    primaryButtonBg: "#ff9d5c",
    primaryButtonText: "#0a0f1f",
    secondaryButtonBg: "#0a0f22",
    secondaryButtonText: "#ffd9bf",
    accentText: "#ff9d5c",
    accentButtonBg: "#ff8a3c",
    accentButtonText: "#0a0f1f",
    borderColor: "rgba(255, 157, 92, 0.3)",
    primaryText: "#ffffff",
    secondaryText: "#c9b7a6",
    tertiaryBg: "rgba(60, 40, 24, 0.4)",
    modalBg: "#0a0f1f",
    separatorLine: "rgba(255, 157, 92, 0.2)",
  },
});

const CONNECT_BTN =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full " +
  "px-4 py-2 sm:px-6 sm:py-2.5 text-sm font-semibold text-[#0a0f1f] " +
  "bg-gradient-to-r from-[#ff7a2f] via-[#ff9d5c] to-[#ffb784] " +
  "border border-[#ff9d5c]/50 shadow-lg shadow-orange-500/30 " +
  "transition-all duration-300 hover:shadow-[0_0_24px_rgba(255,157,92,0.55)] hover:border-[#ffb784] " +
  "disabled:opacity-60 disabled:cursor-not-allowed";

const DETAILS_BTN =
  "inline-flex items-center gap-2 whitespace-nowrap rounded-full " +
  "px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold text-[#0a0f1f] " +
  "bg-gradient-to-r from-[#ff8a3c] via-[#ff9d5c] to-[#ffb784] " +
  "border border-[#ff9d5c]/40 shadow-lg shadow-orange-500/25 " +
  "transition-all duration-300 hover:from-[#ff7a2f] hover:to-[#ffc79e] hover:border-[#ffb784]";

function ConnectWalletButton() {
  const account = useActiveAccount();
  const wallet = useActiveWallet();
  const { disconnect } = useDisconnect();
  const { connect, isConnecting } = useConnectModal();
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const openConnect = async () => {
    try {
      await connect({
        client,
        chain,
        wallets,
        theme: THEME,
        size: "compact",
        title: "Connect to NiftySky",
        showThirdwebBranding: false,
      });
    } catch {
      /* user closed the modal — no-op */
    }
  };

  if (!account) {
    return (
      <button type="button" className={CONNECT_BTN} disabled={isConnecting} onClick={openConnect}>
        {isConnecting ? "Connecting…" : "Connect"}
      </button>
    );
  }

  const addr = account.address;
  const short = `${addr.slice(0, 6)}…${addr.slice(-4)}`;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        className={DETAILS_BTN}
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#10331f", boxShadow: "0 0 6px #34d399" }} />
        {short}
      </button>
      {menuOpen && (
        <div
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 8px)",
            minWidth: 200,
            zIndex: 100,
            background: "linear-gradient(180deg, #0a0f22, #070a18)",
            border: "1px solid var(--hair-2)",
            borderRadius: 6,
            padding: 6,
            boxShadow: "0 18px 50px -18px rgba(0,0,0,0.8)",
          }}
        >
          <div className="mono" style={{ padding: "8px 10px", fontSize: 11, color: "var(--ink-mute)", wordBreak: "break-all" }}>
            {addr}
          </div>
          <button
            type="button"
            role="menuitem"
            className="mono"
            style={menuItemStyle}
            onClick={() => {
              try { navigator.clipboard?.writeText(addr); } catch {}
              setMenuOpen(false);
            }}
          >
            Copy address
          </button>
          <button
            type="button"
            role="menuitem"
            className="mono"
            style={{ ...menuItemStyle, color: "#ff9d9d" }}
            onClick={() => {
              if (wallet) disconnect(wallet);
              setMenuOpen(false);
            }}
          >
            Disconnect
          </button>
        </div>
      )}
    </div>
  );
}

const menuItemStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "9px 10px",
  fontSize: 12,
  letterSpacing: "0.04em",
  color: "var(--ink-soft)",
  background: "transparent",
  border: "none",
  borderRadius: 3,
  cursor: "pointer",
};

export default ConnectWalletButton;
